import type {
  Storyboard,
  StoryboardBoardItem as StoryboardBoardItemType,
  StoryboardBoardSkeletonData,
  StoryboardBoardTrackWithItems,
} from '@/types';

const trackItemsCacheByProject = new Map<
  string,
  Record<string, Record<string, StoryboardBoardItemType>>
>();
const storyboardsCacheByProject = new Map<string, Record<string, Storyboard>>();

export function getTrackItemsBucket(projectId: string) {
  if (!trackItemsCacheByProject.has(projectId)) {
    trackItemsCacheByProject.set(projectId, {});
  }
  return trackItemsCacheByProject.get(projectId)!;
}

export function getStoryboardsBucket(projectId: string) {
  if (!storyboardsCacheByProject.has(projectId)) {
    storyboardsCacheByProject.set(projectId, {});
  }
  return storyboardsCacheByProject.get(projectId)!;
}

export function resolveCachedTrackItemsInFetchedOrder<T extends StoryboardBoardItemType>(
  cachedItems: Record<string, T> | undefined,
  fetchedItems: readonly T[] | undefined
): T[] {
  if (fetchedItems?.length) {
    // 服务端字段优先，避免内存桶旧值盖住重拉结果
    return fetchedItems.map((item) => {
      const cachedItem = cachedItems?.[item.id.toString()];
      return cachedItem ? { ...cachedItem, ...item } : item;
    });
  }
  return cachedItems ? Object.values(cachedItems) : [];
}

/** 用 API 全量分镜列表替换 bucket，避免重新拆解后旧 ID 与新区间叠加导致看板断层 */
export function replaceStoryboardsBucket(projectId: string, storyboards: Storyboard[]): void {
  const next: Record<string, Storyboard> = {};
  for (const sb of storyboards) {
    next[sb.id.toString()] = sb;
  }
  storyboardsCacheByProject.set(projectId, next);
}

/**
 * skeleton 是分镜看板布局的唯一可信 ID 来源；有 skeleton 时只布局其中存在的分镜。
 */
export function getActiveStoryboardIdsFromSkeleton(
  skeleton: StoryboardBoardSkeletonData | undefined
): Set<string> | null {
  const storyboardTrack = skeleton?.tracks?.find((track) => track.type === 'storyboard');
  if (!storyboardTrack?.items?.length) {
    return null;
  }
  return new Set(storyboardTrack.items.map((item) => item.id.toString()));
}

/** 全量看板轨道中的分镜 ID（skeleton 未就绪时的回退来源） */
export function getActiveStoryboardIdsFromBoardTracks(
  tracks: StoryboardBoardTrackWithItems[] | undefined
): Set<string> | null {
  const storyboardTrack = tracks?.find((track) => track.type === 'storyboard');
  if (!storyboardTrack?.items?.length) {
    return null;
  }
  return new Set(storyboardTrack.items.map((item) => (item.storyboardId ?? item.id).toString()));
}

export function resolveActiveStoryboardIdsForLayout(input: {
  skeleton?: StoryboardBoardSkeletonData;
  boardTracks?: StoryboardBoardTrackWithItems[];
}): Set<string> | null {
  return (
    getActiveStoryboardIdsFromSkeleton(input.skeleton) ??
    getActiveStoryboardIdsFromBoardTracks(input.boardTracks)
  );
}

export function clearStoryboardBoardCache(projectId?: string) {
  if (projectId) {
    trackItemsCacheByProject.delete(projectId);
    storyboardsCacheByProject.delete(projectId);
    return;
  }
  trackItemsCacheByProject.clear();
  storyboardsCacheByProject.clear();
}
